import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from 'config/firebase'
import { useAuth } from 'contexts/AuthContext'
import {
  userQuotaConverter,
  UserQuota,
  QuotaEntry,
  QuotaType,
} from '@otw/models'
import { DateTime } from 'luxon'

const QUOTAS_COLLECTION = 'quotas'

/** Document ID for a user's quota of a given type. */
export function getQuotaDocId(userId: string, type: QuotaType): string {
  return `${userId}_${type}`
}

/** Derive current quota from entries (most recent by changedAt). */
export function getCurrentQuotaFromEntries(entries: QuotaEntry[]): number | undefined {
  if (!entries.length) return undefined
  const sorted = [...entries].sort(
    (a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime()
  )
  return sorted[0].value
}

/**
 * Quota in effect for a given month: the most recent entry with changedAt on or before endOfPeriodIso.
 * Use this for historical months when multiple quota changes may exist; the latest one before the period end applies.
 * @param entries - Quota history entries
 * @param endOfPeriodIso - End of the period (e.g. end of selected month) as ISO string
 */
export function getQuotaForMonth(
  entries: QuotaEntry[],
  endOfPeriodIso: string
): number | undefined {
  if (!entries.length) return undefined
  const endMs = new Date(endOfPeriodIso).getTime()
  const onOrBefore = entries
    .filter((e) => new Date(e.changedAt).getTime() <= endMs)
    .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime())
  return onOrBefore.length > 0 ? onOrBefore[0].value : undefined
}

export interface QuotaState {
  /** Current quota (latest entry's value). */
  currentQuota: number | undefined
  /** Full history of quota changes. */
  entries: QuotaEntry[]
}

/**
 * Fetches quota documents for the given user IDs and quota type.
 * Returns a map of userId -> { currentQuota, entries }.
 * The most recent entry is the quota for the current month.
 */
export function useQuotas(userIds: string[], type: QuotaType) {
  const {
    data: quotasMap,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['quotas', type, userIds],
    queryFn: async (): Promise<Record<string, QuotaState>> => {
      if (!userIds.length) return {}

      const result: Record<string, QuotaState> = {}
      await Promise.all(
        userIds.map(async (userId) => {
          const docId = getQuotaDocId(userId, type)
          const ref = doc(db, QUOTAS_COLLECTION, docId).withConverter(userQuotaConverter)
          const snap = await getDoc(ref)
          const data = snap.exists() ? snap.data() : null
          const entries = data?.entries ?? []
          const currentQuota = getCurrentQuotaFromEntries(entries)
          result[userId] = { currentQuota, entries }
        })
      )
      return result
    },
    enabled: userIds.length > 0,
    staleTime: 1000 * 60 * 2, // 2 minutes
  })

  return {
    quotas: quotasMap ?? {},
    isLoading,
    isError,
    error,
    refetch,
  }
}

/**
 * Mutation to set a user's quota. Appends a new entry with value, changedAt (now), and changedBy (current user).
 * Creates the quota document if it doesn't exist.
 */
export function useSetQuota(type: QuotaType) {
  const queryClient = useQueryClient()
  const { userInfo } = useAuth()

  return useMutation({
    mutationFn: async ({
      userId,
      value,
    }: {
      userId: string
      value: number
    }): Promise<UserQuota> => {
      if (!userId || !userInfo?.id) {
        throw new Error('User ID and current user are required')
      }
      if (typeof value !== 'number' || value < 0) {
        throw new Error('Quota value must be a non-negative number')
      }

      const now = DateTime.now().toISO()
      const docId = getQuotaDocId(userId, type)
      const ref = doc(db, QUOTAS_COLLECTION, docId).withConverter(userQuotaConverter)
      const existing = await getDoc(ref)
      const existingData = existing.exists() ? existing.data() : null
      const entries: QuotaEntry[] = [...(existingData?.entries ?? []), {
        value,
        changedAt: now,
        changedBy: userInfo.id,
      }]

      const userQuota: UserQuota = {
        id: docId,
        userId,
        type,
        entries,
      }
      await setDoc(ref, userQuota, { merge: true })
      return userQuota
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quotas', type] })
      queryClient.invalidateQueries({ queryKey: ['quotas', type, [variables.userId]] })
      queryClient.invalidateQueries({ queryKey: ['users', 'department', 'inventory'] })
      queryClient.invalidateQueries({ queryKey: ['users', 'department', 'sales'] })
    },
  })
}
